import { useCallback, useEffect, useState } from "react";
import { ConfirmModal } from "../lib/ConfirmModal.tsx";
import { REGION_LABELS, REGIONS } from "../../../src/projects/region.ts";
import { SANDBOX_BACKENDS } from "../../../src/sandbox/sandbox-backend.ts";
import { fetchJson } from "../lib/fetchJson.ts";
import { day, Refusal } from "./bits.tsx";

// The Organisations screen: every customer Organisation as a summary row with
// Disable and Enable, a sandbox backend override picker and a region picker.
// Nothing opens: an operator who needs a customer's work joins the
// Organisation as a member. See ADR-0039 and ADR-0043.

/** One customer Organisation as `GET /api/organisations` lists it. `sandboxBackend` is null when the platform default applies. */
export type OrganisationRow = {
  id: string;
  name: string;
  createdAt: string;
  disabledAt: string | null;
  sandboxBackend: string | null;
  region: string;
  memberCount: number;
  projectCount: number;
  runsInFlight: number;
};

/**
 * The Organisations screen: the summary table, one row per Organisation with
 * its counts, state, backend override and region, each picker writing on
 * change. Reads `/api/organisations` on mount and after every write on a
 * row. Rendered by the admin shell at `/`.
 */
export function OrganisationsPage(): JSX.Element {
  const [rows, setRows] = useState<OrganisationRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await fetchJson<{ organisations: OrganisationRow[] }>("/api/organisations");
      setRows(body.organisations);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  let table: JSX.Element;
  if (rows === null) {
    table = <p aria-live="polite">Loading…</p>;
  } else if (rows.length === 0) {
    table = <p className="empty">No Organisation has signed up yet.</p>;
  } else {
    table = (
      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Created</th>
            <th>Members</th>
            <th>Projects</th>
            <th>Runs in flight</th>
            <th>State</th>
            <th>Sandbox backend</th>
            <th>Region</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <OrganisationLine key={o.id} o={o} onChanged={load} />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <>
      <p className="lede">
        Every customer Organisation, as a summary. Disabling one takes it out of its members'
        reach, kills its Runs and holds its Requests; nothing is deleted, and enabling it puts it
        back — its Requests stay held until a member resumes them. The sandbox backend override
        and the region are yours to set here and nowhere else: an override pins the Organisation
        to one backend, the platform default follows the Platform screen, and the region is where
        its sandboxes run. Either change reaches the next Run, never a running one.
      </p>

      <Refusal message={loadError} />

      <section className="panel" aria-label="organisations">
        <div className="panel-head">
          <h2>Organisations</h2>
          {rows ? <span className="meta">{rows.filter((o) => o.disabledAt === null).length} active</span> : null}
        </div>
        <div className="panel-body flush scroll-x">{table}</div>
      </section>
    </>
  );
}

function OrganisationLine({ o, onChanged }: { o: OrganisationRow; onChanged: () => Promise<void> }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabled = o.disabledAt !== null;

  // Every write on the row is one shape: the request, then the reload, the
  // server's words on a refusal.
  async function write(path: string, init: RequestInit): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fetchJson(`/api/organisations/${encodeURIComponent(o.id)}/${path}`, init);
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  const verb = (which: "disable" | "enable") => write(which, { method: "POST" });

  const put = (path: string, body: unknown) =>
    write(path, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

  // "" is the platform default: the row's DELETE, not a PUT of a magic
  // string.
  const setBackend = (value: string) =>
    value === "" ? write("sandbox-backend", { method: "DELETE" }) : put("sandbox-backend", { backend: value });

  const setRegion = (value: string) => put("region", { region: value });

  let action: JSX.Element;
  if (disabled) {
    action = (
      <button type="button" className="btn ghost xs" disabled={busy} onClick={() => void verb("enable")}>
        {busy ? "Enabling…" : "Enable"}
      </button>
    );
  } else {
    action = (
      <>
        <button type="button" className="btn ghost xs" onClick={() => setConfirming(true)}>
          Disable
        </button>
        <ConfirmModal
          open={confirming}
          title="Disable Organisation"
          heading={<>Disable {o.name}?</>}
          confirmLabel="Confirm disable"
          busyLabel="Disabling…"
          busy={busy}
          error={error}
          onConfirm={() => void verb("disable")}
          onCancel={() => setConfirming(false)}
        >
          <p>
            Its members are signed out and cannot sign in, its live Runs are killed and every
            Request is held. Nothing is deleted; Enable puts it back.
          </p>
        </ConfirmModal>
      </>
    );
  }

  return (
    <tr>
      <td>{o.name}</td>
      <td>{day(o.createdAt)}</td>
      <td>{o.memberCount}</td>
      <td>{o.projectCount}</td>
      <td>{o.runsInFlight}</td>
      <td>
        <span className={disabled ? "pill bad" : "pill ok"} title={disabled ? `since ${day(o.disabledAt)}` : undefined}>
          {disabled ? "disabled" : "active"}
        </span>
      </td>
      <td>
        <select
          className="cell"
          aria-label="Sandbox backend"
          value={o.sandboxBackend ?? ""}
          disabled={busy}
          onChange={(e) => void setBackend(e.target.value)}
        >
          <option value="">platform default</option>
          {SANDBOX_BACKENDS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          className="cell"
          aria-label="Region"
          value={o.region}
          disabled={busy}
          onChange={(e) => void setRegion(e.target.value)}
        >
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r} — {REGION_LABELS[r]}
            </option>
          ))}
        </select>
      </td>
      <td>
        {action}
        <Refusal message={error} />
      </td>
    </tr>
  );
}
